import React, { FC, ReactElement, ReactNode, useEffect, useRef, useState } from 'react'
import { BrowserRouter, Route, Switch } from 'react-router-dom'
import { useEventSource, useEventSourceListener } from '@react-nano/use-event-source'

import { Layout, Notifications } from '@infologistics/frontend-libraries'
import { LayoutWrapper, Menu, PrivateRoute, RedirectToStartPage } from '@containers'
import Page403 from '@views/misc/components/Page403'
import Page404 from '@views/misc/components/Page404'
import Page500 from '@views/misc/components/Page500'

import TokensService from '@services/tokensService'

import { displayErrorNotification, getIsInterfaceDisabled, getRoutesList } from '@utils/utils'

import {
  BASE_URL,
  BrowserStorage,
  FILTERS_DATA_UPDATE_INTERVAL,
  Instance,
  isDevelopment,
  Path,
  SSEMessageType,
  isMasterBranch,
} from '@const/consts'

import { authRoutes } from '@app/routes'

import { IApplicationState, Nullable } from '@store/types/commonTypes'
import {
  ILayoutRouterProps as IProps,
  ISSEMessage
} from './types'
import PageErrorOrgs from '@app/views/misc/components/PageErrorOrgs'
import { CSSTransition } from 'react-transition-group'
import { useSelector } from 'react-redux'
import { Preview } from '@app/views/organization/documents/components/DocumentEdit/components/Preview/Preview'
import LoginLayout from '@app/views/auth/components/LoginLayout'
import { MobileAppNotification } from '../MobileAppNotification'
import AiAssistant from '@common/AiAssistant'

const AI_ASSISTANT_ALLOWED_EMAIL_DOMAINS = ['infologistics.ru', 'ilsglobal.ie', 'ilsglobal.vn']

const LayoutRouter: FC<IProps> = (props) => {
  const {
    isError403,
    isError404,
    isError500,
    isErrorOrgs,
    isRootMode,
    onGetAllBadges,
    onGetBadges,
    onGetBadgesNotices,
    onSetInstance,
    onToggleFetchFiltersData,
  } = props

  const userFromService = TokensService.getUserFromCookies()
  const accessToken = TokensService.getTokenFromCookies(BrowserStorage.TOKEN_ACCESS, userFromService)
  const refreshToken = TokensService.getTokenFromCookies(BrowserStorage.TOKEN_REFRESH, userFromService)
  const federationToken = TokensService.getTokenFromCookies(BrowserStorage.TOKEN_FEDERATION, userFromService)
  const isAuthenticated = !!accessToken
  const hasActiveSession = !!userFromService && federationToken !== null && (!!accessToken || !!refreshToken)
  const userEmail = useSelector((state: IApplicationState) => state.user.profile.email)
  const normalizedUserEmailDomain = getEmailDomain(userEmail ?? '')
  const instance =  useSelector((state: IApplicationState) => state.utils.instance);
  const isAiAssistantAllowed =
    !!normalizedUserEmailDomain &&
    instance === Instance.RUSSIAN &&
    (AI_ASSISTANT_ALLOWED_EMAIL_DOMAINS.includes(normalizedUserEmailDomain) || (isDevelopment && !isMasterBranch));

  const eventSourceRef = useRef<Nullable<EventSource>>(null)

  const [fetchFiltersData, setFetchFiltersData] = useState(0)

  const getPath = (): string => isRootMode ? '' : Path.ORGANIZATION_ROUTE

  const { show } = useSelector((state: IApplicationState) => state.filePreview);

  const getPrivateRoutes = (): ReactElement => (
    <Switch>
      <Route path={[Path.SIGN_IN, Path.FORGOT, Path.NEP_VERIFY]} component={LoginLayout} />

      {getRoutesList(authRoutes)}

      <PrivateRoute exact path={getPath()} component={LayoutWrapper} isAuthenticated={isAuthenticated} />

      <Route component={RedirectToStartPage} />

      {/* <Route component={Page404} path={"*"} /> */}
    </Switch>
  );

  const handleGetBadges = (): void => {
    if (getIsInterfaceDisabled()) return

    if (!isRootMode) onGetAllBadges().catch(displayErrorNotification)
    onGetBadges().catch(displayErrorNotification)
    onGetBadgesNotices().catch(displayErrorNotification)
  }

  const handleToggleFetchFilterData = (): void => {
    if (getIsInterfaceDisabled()) return

    onToggleFetchFiltersData(true)
  }

  const renderPreview = (): ReactNode => {
    const classNames = {
      enter: 'fl-modal-enter',
      enterActive: 'fl-modal-enter_active',
      exit: 'fl-modal-exit',
      exitActive: 'fl-modal-exit_active',
    };

    const TIMEOUT = 300;

    return (
      <CSSTransition
        in={show}
        timeout={TIMEOUT}
        classNames={classNames}
        unmountOnExit={true}
      >
        {show ? <Preview /> : <></>}
      </CSSTransition>
    );
  };

  const renderRoutes = (): ReactElement => {
    if (isError403) {
      return (
        <Layout>
          <Route component={Page403} />
        </Layout>
      )
    }

    if (isError404) {
      return (
        <Layout>
          <Route component={Page404} />
        </Layout>
      )
    }

    if (isError500) {
      return (
        <Layout>
          <Route component={Page500} />
        </Layout>
      )
    }

    if (isErrorOrgs) {
      return (
        <Layout>
          <Route component={PageErrorOrgs} />
        </Layout>
      )
    }

    return getPrivateRoutes()
  }

  const eventSourceBaseURL = `${BASE_URL}services/push/events/subscribe`
  const eventSourceURL = accessToken
    ? `${eventSourceBaseURL}?token=${accessToken}`
    : ''

  const [eventSource] = useEventSource(eventSourceURL)

  useEventSourceListener(eventSource, ['message'], ({data}) => {
    const { type } = JSON.parse(data) as ISSEMessage

    if (type !== SSEMessageType.NOTIFICATION) return

    handleGetBadges()
  })

  useEffect(() => {
    eventSourceRef.current = eventSource
  }, [eventSource])

  useEffect(() => {
    if (isAuthenticated) {
      const fetchFiltersData = window.setInterval(handleToggleFetchFilterData, FILTERS_DATA_UPDATE_INTERVAL)

      setFetchFiltersData(fetchFiltersData)
    }

    if (!isDevelopment) {
      const instance = window.location.hostname.match(/com.br|ilsbrasil.com/g)
        ? Instance.BRASIL
        : window.location.hostname.match(/ilsglobal.vn/g)
          ? Instance.VIETNAMESE
          : window.location.hostname.match(/metrofile.co.za|metrofile.co.ke|africa/g)?.[0]
            ? Instance.SOUTH_AFRICA
            : (window.location.hostname.match(/global|ru/g)?.[0] ??
              window.localStorage.getItem(BrowserStorage.INSTANCE) ??
              Instance.GLOBAL);

      onSetInstance(instance)
      window.localStorage.setItem(BrowserStorage.INSTANCE, instance)
    }

    return () => {
      fetchFiltersData && clearInterval(fetchFiltersData)

      eventSourceRef.current?.close()
    }
  }, [])

  return (
    <BrowserRouter>
      <div>
        <Notifications />
        <Route exact path={getPath()} component={Menu} />
        {renderRoutes()}
        {renderPreview()}
        <MobileAppNotification />
        {hasActiveSession && isAiAssistantAllowed && <AiAssistant />}
      </div>
    </BrowserRouter>
  )
}

const getEmailDomain = (email?: string): string => {
  const parts = email?.trim().toLowerCase().split('@')

  return parts?.length === 2 ? parts[1] : ''
}

export default LayoutRouter
